'use client';

import { motion } from 'framer-motion';
import { detectRegion, getRegionColors, getRegionFlag, regionNames, type Region } from '../utils/regionColors';

export interface RegionIndicatorProps {
  /** Location string to detect region from, or explicit region */
  location?: string;
  region?: Region;
  /** Display variant */
  variant?: 'badge' | 'bar' | 'dot' | 'minimal' | 'flag';
  /** Size */
  size?: 'sm' | 'md' | 'lg';
  /** Show country flag */
  showFlag?: boolean;
  /** Show location text */
  showText?: boolean;
  /** Locale for text */
  locale?: 'en' | 'fr';
  /** Custom className */
  className?: string;
  /** Position for bar variant */
  position?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Stone Investment Region Indicator
 * 
 * A subtle visual element that indicates whether a property
 * is located in Provence (France) or Mauritius.
 * 
 * Uses color-coding:
 * - Provence: Lavender/terracotta tones
 * - Mauritius: Ocean teal/turquoise tones
 */
export function RegionIndicator({
  location,
  region: explicitRegion,
  variant = 'badge',
  size = 'md',
  showFlag = true,
  showText = true,
  locale = 'en',
  className = '',
  position = 'left',
}: RegionIndicatorProps) {
  // Determine region
  const region = explicitRegion || detectRegion(location);
  const colors = getRegionColors(region);
  const flag = getRegionFlag(region);
  const name = regionNames[region][locale];

  // Size classes
  const sizeClasses = {
    sm: 'text-[9px] tracking-[0.2em]',
    md: 'text-[10px] tracking-[0.25em]',
    lg: 'text-xs tracking-[0.2em]',
  };

  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  };

  const barSizes = {
    sm: position === 'left' || position === 'right' ? 'w-0.5' : 'h-0.5',
    md: position === 'left' || position === 'right' ? 'w-1' : 'h-1',
    lg: position === 'left' || position === 'right' ? 'w-1.5' : 'h-1.5',
  };

  // Badge variant - Most commonly used
  if (variant === 'badge') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`inline-flex items-center gap-2 ${className}`}
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {/* Colored dot indicator */}
        <div 
          className={`${dotSizes[size]} rounded-full`}
          style={{ backgroundColor: colors.primary }}
        />
        
        {/* Flag */}
        {showFlag && (
          <span className="text-sm">{flag}</span>
        )}
        
        {/* Text */}
        {showText && (
          <span 
            className={`uppercase ${sizeClasses[size]}`}
            style={{ color: colors.text }}
          >
            {name}
          </span>
        )}
      </motion.div>
    );
  }

  // Bar variant - Subtle accent line on cards
  if (variant === 'bar') {
    const barPositionClasses = {
      top: 'top-0 left-0 right-0 h-full',
      bottom: 'bottom-0 left-0 right-0 h-full',
      left: 'top-0 bottom-0 left-0 w-full',
      right: 'top-0 bottom-0 right-0 w-full',
    };

    return (
      <div 
        className={`absolute ${barPositionClasses[position]} ${barSizes[size]} ${className}`}
        style={{ 
          background: colors.gradient,
        }}
      />
    );
  }

  // Dot variant - Just a small colored dot
  if (variant === 'dot') {
    return (
      <div 
        className={`${dotSizes[size]} rounded-full ${className}`}
        style={{ backgroundColor: colors.primary }}
        title={name}
      />
    );
  }

  // Minimal variant - Just text with color
  if (variant === 'minimal') {
    return (
      <span 
        className={`uppercase ${sizeClasses[size]} ${className}`}
        style={{ 
          color: colors.text,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {showFlag && <span className="mr-1.5">{flag}</span>}
        {showText && name}
      </span>
    );
  }

  // Flag variant - Just the flag with optional tooltip
  if (variant === 'flag') {
    return (
      <span 
        className={`${className}`}
        title={name}
      >
        {flag}
      </span>
    );
  }

  return null;
}

/**
 * Region Badge - Styled location badge with region color
 */
export function RegionBadge({
  location,
  region: explicitRegion,
  locale = 'en',
  className = '',
}: {
  location?: string;
  region?: Region;
  locale?: 'en' | 'fr';
  className?: string;
}) {
  const region = explicitRegion || detectRegion(location);
  const colors = getRegionColors(region);
  const flag = getRegionFlag(region);
  const displayName = location || regionNames[region][locale];

  return (
    <div 
      className={`inline-flex items-center gap-2 px-4 py-2 border ${className}`}
      style={{ 
        borderColor: colors.primary + '40',
        backgroundColor: colors.background,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <span className="text-sm">{flag}</span>
      <span 
        className="text-[10px] tracking-[0.25em] uppercase"
        style={{ color: colors.text }}
      >
        {displayName}
      </span>
    </div>
  );
}
